import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Input, Select, DatePicker, TimePicker, InputNumber, Typography, message, Divider, Space, Tag, Alert } from 'antd';
import { CopyOutlined, SendOutlined, UserOutlined, TeamOutlined, CompassOutlined, CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { colors, spacing, radius, typography, shadows, transitions } from '../../styles/monochromeTheme';
import { supabase } from '../../api/supabase';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Styles
const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xl,
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: spacing.xl,
    paddingBottom: spacing.lg,
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  card: {
    background: colors.bgSecondary,
    borderRadius: radius.xl,
    border: `1px solid ${colors.borderLight}`,
    boxShadow: shadows.md,
    padding: spacing.xl,
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textPrimary,
    marginBottom: spacing.md,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
  },
  crewCard: {
    background: colors.bgTertiary,
    borderRadius: radius.lg,
    padding: spacing.md,
    marginBottom: spacing.sm,
    border: `1px solid ${colors.borderLight}`,
  },
  primaryBtn: {
    background: colors.bgDark,
    color: colors.textInverse,
    border: 'none',
    borderRadius: radius.lg,
    fontWeight: typography.fontWeight.semibold,
    height: '44px',
    boxShadow: shadows.sm,
  },
  secondaryBtn: {
    background: colors.bgSecondary,
    color: colors.textPrimary,
    border: `1px solid ${colors.borderMedium}`,
    borderRadius: radius.lg,
    fontWeight: typography.fontWeight.medium,
    height: '40px',
  },
  previewBox: {
    background: colors.bgTertiary,
    borderRadius: radius.lg,
    padding: spacing.lg,
    fontFamily: 'monospace',
    fontSize: typography.fontSize.sm,
    whiteSpace: 'pre-wrap',
    maxHeight: '400px',
    overflow: 'auto',
    border: `1px solid ${colors.borderLight}`,
  },
  tag: {
    borderRadius: radius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontWeight: typography.fontWeight.medium,
  },
};

// Activities list for Gustavia
const ACTIVITIES = [
  { value: 'charter', label: 'Charter' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'excursion', label: 'Excursion' },
  { value: 'promenade', label: 'Promenade en mer' },
];

// Nationalities
const NATIONALITIES = [
  { value: 'FR', label: 'Française' },
  { value: 'US', label: 'Américaine' },
  { value: 'GB', label: 'Britannique' },
  { value: 'DE', label: 'Allemande' },
  { value: 'IT', label: 'Italienne' },
  { value: 'ES', label: 'Espagnole' },
  { value: 'CH', label: 'Suisse' },
  { value: 'BE', label: 'Belge' },
  { value: 'NL', label: 'Néerlandaise' },
  { value: 'CA', label: 'Canadienne' },
  { value: 'BR', label: 'Brésilienne' },
  { value: 'OTHER', label: 'Autre' },
];

function ClairancePage() {
  const [form] = Form.useForm();
  const [yachts, setYachts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [crewMembers, setCrew] = useState([{ nom: '', prenom: '', birthDate: '', birthPlace: '', passport: '', nationality: 'FR' }]);
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Fetch yachts and employees
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch yachts
        const { data: yachtsData } = await supabase
          .from('yachts')
          .select('id, name, brand, length_feet')
          .order('name');

        if (yachtsData) {
          setYachts(yachtsData);
        }

        // Fetch active employees (crew)
        const { data: employeesData } = await supabase
          .from('employees')
          .select('id, name, role')
          .eq('status', 'active')
          .order('name');

        if (employeesData) {
          setEmployees(employeesData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Add crew member
  const addCrewMember = () => {
    setCrew([...crewMembers, { nom: '', prenom: '', birthDate: '', birthPlace: '', passport: '', nationality: 'FR' }]);
  };

  // Remove crew member
  const removeCrewMember = (index) => {
    if (crewMembers.length > 1) {
      setCrew(crewMembers.filter((_, i) => i !== index));
    }
  };

  // Update crew member
  const updateCrewMember = (index, field, value) => {
    const updated = [...crewMembers];
    updated[index][field] = value;
    setCrew(updated);
  };

  // Add passenger
  const addPassenger = () => {
    setPassengers([...passengers, { nom: '', prenom: '', birthDate: '', birthPlace: '', passport: '', nationality: 'FR' }]);
  };

  // Remove passenger
  const removePassenger = (index) => {
    setPassengers(passengers.filter((_, i) => i !== index));
  };

  // Update passenger
  const updatePassenger = (index, field, value) => {
    const updated = [...passengers];
    updated[index][field] = value;
    setPassengers(updated);
  };

  // Generate preview data
  const generatePreview = () => {
    const values = form.getFieldsValue();

    const data = {
      formData: {
        departureDate: values.departureDate ? values.departureDate.format('DD/MM/YYYY') : '',
        departureTime: values.departureTime ? values.departureTime.format('HH:mm') : '',
        activity: values.activity || '',
        berth: values.berth || '',
        yacht: values.yacht || '',
        destination: values.destination || '',
      },
      crew: crewMembers.filter(m => m.nom || m.prenom),
      passengers: passengers.filter(p => p.nom || p.prenom),
    };

    setPreviewData(data);

    // Store in localStorage for extension
    localStorage.setItem('msp_gustavia_complete', JSON.stringify(data));
    message.success('Données prêtes pour Gustavia !');
  };

  // Copy JSON to clipboard
  const copyToClipboard = () => {
    if (previewData) {
      navigator.clipboard.writeText(JSON.stringify(previewData, null, 2));
      message.success('Données copiées !');
    }
  };

  // Person form fields component
  const PersonFields = ({ person, index, onUpdate, onRemove, type }) => (
    <div style={styles.crewCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm }}>
        <Tag style={{ ...styles.tag, background: type === 'crew' ? colors.bgDark : '#1890ff', color: colors.textInverse }}>
          {type === 'crew' ? `Équipier #${index + 1}` : `Passager #${index + 1}`}
        </Tag>
        {((type === 'crew' && crewMembers.length > 1) || type === 'passenger') && (
          <Button type="text" danger size="small" onClick={() => onRemove(index)}>
            Supprimer
          </Button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: spacing.md }}>
        <Input
          placeholder="Nom"
          value={person.nom}
          onChange={(e) => onUpdate(index, 'nom', e.target.value.toUpperCase())}
        />
        <Input
          placeholder="Prénom"
          value={person.prenom}
          onChange={(e) => onUpdate(index, 'prenom', e.target.value)}
        />
        <DatePicker
          placeholder="Date de naissance"
          format="DD/MM/YYYY"
          value={person.birthDate ? dayjs(person.birthDate, 'DD/MM/YYYY') : null}
          onChange={(date) => onUpdate(index, 'birthDate', date ? date.format('DD/MM/YYYY') : '')}
          style={{ width: '100%' }}
        />
        <Input
          placeholder="Lieu de naissance"
          value={person.birthPlace}
          onChange={(e) => onUpdate(index, 'birthPlace', e.target.value)}
        />
        <Input
          placeholder="N° Passeport"
          value={person.passport}
          onChange={(e) => onUpdate(index, 'passport', e.target.value.toUpperCase())}
        />
        <Select
          placeholder="Nationalité"
          value={person.nationality}
          onChange={(value) => onUpdate(index, 'nationality', value)}
          style={{ width: '100%' }}
        >
          {NATIONALITIES.map(n => (
            <Option key={n.value} value={n.value}>{n.label}</Option>
          ))}
        </Select>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>🚢</div>
          <div>
            <Title level={3} style={{ margin: 0, color: colors.textPrimary }}>
              Clairance Gustavia
            </Title>
            <Text style={{ color: colors.textMuted }}>
              Préparer les données pour le formulaire de sortie de port
            </Text>
          </div>
        </div>

        <Alert
          message="Extension Chrome requise"
          description="Cette page prépare les données pour l'auto-remplissage du formulaire Gustavia. Utilisez l'extension Chrome 'Master Ski Pilou' sur le site de la capitainerie."
          type="info"
          showIcon
          style={{ marginBottom: spacing.lg, borderRadius: radius.lg }}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.xl }}>
          {/* Left Column - Form */}
          <div>
            {/* Departure Info */}
            <div style={styles.card}>
              <div style={styles.sectionTitle}>
                <CalendarOutlined /> Informations de départ
              </div>
              <Form form={form} layout="vertical">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                  <Form.Item name="departureDate" label="Date de départ">
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="departureTime" label="Heure de départ">
                    <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={15} />
                  </Form.Item>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                  <Form.Item name="yacht" label="Bateau">
                    <Select placeholder="Sélectionner le bateau" allowClear showSearch optionFilterProp="children">
                      {yachts.map(y => (
                        <Option key={y.id} value={y.name}>{y.name} ({y.brand})</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="activity" label="Activité">
                    <Select placeholder="Type d'activité">
                      {ACTIVITIES.map(a => (
                        <Option key={a.value} value={a.value}>{a.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                  <Form.Item name="berth" label="Emplacement">
                    <Input placeholder="Ex: Quai Gustavia A12" />
                  </Form.Item>
                  <Form.Item name="destination" label="Destination">
                    <Input placeholder="Ex: Tour de l'île, Colombier..." />
                  </Form.Item>
                </div>
              </Form>
            </div>

            {/* Crew */}
            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
                <div style={styles.sectionTitle}>
                  <UserOutlined /> Équipage ({crewMembers.length})
                </div>
                <Button onClick={addCrewMember} style={styles.secondaryBtn}>
                  + Ajouter équipier
                </Button>
              </div>

              {crewMembers.map((member, index) => (
                <PersonFields
                  key={index}
                  person={member}
                  index={index}
                  onUpdate={updateCrewMember}
                  onRemove={removeCrewMember}
                  type="crew"
                />
              ))}
            </div>

            {/* Passengers */}
            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
                <div style={styles.sectionTitle}>
                  <TeamOutlined /> Passagers ({passengers.length})
                </div>
                <Button onClick={addPassenger} style={styles.secondaryBtn}>
                  + Ajouter passager
                </Button>
              </div>

              {passengers.length === 0 ? (
                <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: spacing.lg }}>
                  Aucun passager ajouté
                </Text>
              ) : (
                passengers.map((passenger, index) => (
                  <PersonFields
                    key={index}
                    person={passenger}
                    index={index}
                    onUpdate={updatePassenger}
                    onRemove={removePassenger}
                    type="passenger"
                  />
                ))
              )}
            </div>
          </div>

          {/* Right Column - Preview & Actions */}
          <div>
            <div style={styles.card}>
              <div style={styles.sectionTitle}>
                <SendOutlined /> Actions
              </div>

              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={generatePreview}
                  style={{ ...styles.primaryBtn, width: '100%' }}
                  size="large"
                >
                  Préparer pour Gustavia
                </Button>

                {previewData && (
                  <Button
                    icon={<CopyOutlined />}
                    onClick={copyToClipboard}
                    style={{ ...styles.secondaryBtn, width: '100%' }}
                  >
                    Copier les données JSON
                  </Button>
                )}
              </Space>

              {previewData && (
                <>
                  <Divider />
                  <div style={styles.sectionTitle}>
                    Aperçu des données
                  </div>
                  <div style={styles.previewBox}>
                    {JSON.stringify(previewData, null, 2)}
                  </div>

                  <Alert
                    message="Données prêtes !"
                    description="Ouvrez le formulaire Gustavia et cliquez sur le bouton 'Auto-remplir' de l'extension."
                    type="success"
                    showIcon
                    style={{ marginTop: spacing.md, borderRadius: radius.md }}
                  />
                </>
              )}
            </div>

            {/* Quick crew selection from employees */}
            <div style={styles.card}>
              <div style={styles.sectionTitle}>
                <CompassOutlined /> Équipage rapide
              </div>
              <Text type="secondary" style={{ display: 'block', marginBottom: spacing.md }}>
                Sélectionner depuis la liste des employés :
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
                {employees.filter(e => e.role?.toLowerCase().includes('capitaine') || e.role?.toLowerCase().includes('marin') || e.role?.toLowerCase().includes('seabob')).map(emp => (
                  <Tag
                    key={emp.id}
                    style={{ ...styles.tag, cursor: 'pointer', background: colors.bgTertiary }}
                    onClick={() => {
                      const names = emp.name.split(' ');
                      const newMember = {
                        nom: names.slice(1).join(' ').toUpperCase() || names[0].toUpperCase(),
                        prenom: names[0] || '',
                        birthDate: '',
                        birthPlace: '',
                        passport: '',
                        nationality: 'FR',
                      };
                      setCrew([...crewMembers.filter(m => m.nom || m.prenom), newMember]);
                      message.success(`${emp.name} ajouté à l'équipage`);
                    }}
                  >
                    + {emp.name}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClairancePage;
